

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/Auth';
import {
  Card,
  Grid,
  Box,
  CircularProgress,
  Tabs,
  Tab,
  Typography,
  Divider,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Paper,
  IconButton,
  Tooltip
} from "@mui/material";
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  AttachMoney as AttachMoneyIcon,
  SupportAgent as SupportAgentIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Event as EventIcon,
  Description as DescriptionIcon,
  ShoppingCart as ShoppingCartIcon,
  Receipt as ReceiptIcon,
  ArrowBack as ArrowBackIcon,
  Timeline as TimelineIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';

// Import custom components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import TimelineItem from "examples/Timeline/TimelineItem";
import TimelineList from "examples/Timeline/TimelineList";
import DataLoadingSpinner from "components/Spinners/DataLoadingSpinner";
import { useCurrency } from '../../context/CurrencyContext';
import { useTravelUi } from '../../hooks/useTravelUi';
import { useTheme } from '@mui/material/styles';

// Import API functions
import {
  fetchAccountContacts,
  fetchAccountDeals,
  fetchAccountCases,
  fetchAccountActivities,
  fetchAccountCommunications,
  fetchAccountDocuments,
  fetchAccountOrders,
  fetchAccountInvoices
} from '../../api/customer360';

// Import utility functions
import { formatDate } from 'utils/DateTimeConversion';

// TabPanel component for tab content
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-360-tabpanel-${index}`}
      aria-labelledby={`customer-360-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function Customer360View() {
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const { showTravel, loading: travelUiLoading } = useTravelUi();
  const [account, setAccount] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // String keys so inserting/removing the Orders tab never shifts selection.
  const [tabValue, setTabValue] = useState('summary');
  const [data, setData] = useState({
    contacts: [],
    deals: [],
    cases: [],
    activities: [],
    communications: [],
    documents: [],
    orders: [],
    invoices: []
  });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const { currency, getCurrencyLabel, availableCurrencies, formatCurrency  } = useCurrency();
  // Hide legacy Orders until travel UI is known, then when travel is on
  // (classic only: v2 keeps them in the Sales app for travel tenants).
  const isV2 = useTheme()?.pearlUi === 'v2';
  const showLegacyOrders = !travelUiLoading && (!showTravel || isV2);

  // Fetch account details
  useEffect(() => {
    const fetchAccountData = async () => {
      try {
        const response = await axiosInstance.get(`/accounts/${id}`);
        setAccount(response.data);
      } catch (error) {
        console.error('Error fetching account data:', error);
      }
    };

    fetchAccountData();
  }, [id, axiosInstance, refreshTrigger]);

  // Fetch all 360° view data
  useEffect(() => {
    const fetchAllData = async () => {
      setIsLoading(true);
      try {
        // Legacy account orders are hidden when Travel UI is on.
        const [
          contactsData,
          dealsData,
          casesData,
          activitiesData,
          communicationsData,
          documentsData,
          ordersData,
          invoicesData
        ] = await Promise.all([
          fetchAccountContacts(id, axiosInstance),
          fetchAccountDeals(id, axiosInstance),
          fetchAccountCases(id, axiosInstance),
          fetchAccountActivities(id, axiosInstance),
          fetchAccountCommunications(id, axiosInstance),
          fetchAccountDocuments(id, axiosInstance),
          showLegacyOrders ? fetchAccountOrders(id, axiosInstance) : Promise.resolve([]),
          fetchAccountInvoices(id, axiosInstance, { showTravel }),
        ]);

        setData({
          contacts: contactsData || [],
          deals: dealsData || [],
          cases: casesData || [],
          activities: activitiesData || [],
          communications: communicationsData || [],
          documents: documentsData || [],
          orders: ordersData || [],
          invoices: invoicesData || []
        });
      } catch (error) {
        console.error('Error fetching 360° view data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (id && !travelUiLoading) {
      fetchAllData();
    }
  }, [id, refreshTrigger, showLegacyOrders, showTravel, travelUiLoading, axiosInstance]);

  useEffect(() => {
    if (tabValue === 'orders' && !showLegacyOrders) {
      setTabValue('invoices');
    }
  }, [showLegacyOrders, tabValue]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleBackToAccount = () => {
    navigate(`/accounts/${id}`);
  };

  // Render functions for each tab
  const renderAccountSummary = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            height: '100%',
            borderRadius: '16px',
            background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
            boxShadow: '10px 10px 20px #e6e6e6, -10px -10px 20px #ffffff',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '15px 15px 30px #e0e0e0, -15px -15px 30px #ffffff',
            }
          }}
        >
          <MDTypography variant="h6" gutterBottom fontWeight="bold" color="primary">Account Information</MDTypography>
          <Divider sx={{ mb: 2, borderColor: 'primary.light' }} />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <MDTypography variant="subtitle2">Name</MDTypography>
              <MDTypography variant="body2">{account?.name || ''}</MDTypography>
            </Grid>
            <Grid item xs={6}>
              <MDTypography variant="subtitle2">Type</MDTypography>
              <MDTypography variant="body2">{account?.account_type || ''}</MDTypography>
            </Grid>
            <Grid item xs={6}>
              <MDTypography variant="subtitle2">Industry</MDTypography>
              <MDTypography variant="body2">{account?.industry || ''}</MDTypography>
            </Grid>
            <Grid item xs={6}>
              <MDTypography variant="subtitle2">Status</MDTypography>
              <MDTypography variant="body2">{account?.account_status || ''}</MDTypography>
            </Grid>
            <Grid item xs={6}>
              <MDTypography variant="subtitle2">Website</MDTypography>
              <MDTypography variant="body2">{account?.website || ''}</MDTypography>
            </Grid>
            <Grid item xs={6}>
              <MDTypography variant="subtitle2">Phone</MDTypography>
              <MDTypography variant="body2">{account?.phone || ''}</MDTypography>
            </Grid>
            <Grid item xs={6}>
              <MDTypography variant="subtitle2">Email</MDTypography>
              <MDTypography variant="body2">{account?.email || ''}</MDTypography>
            </Grid>
            <Grid item xs={6}>
              <MDTypography variant="subtitle2">Annual Revenue</MDTypography>
              <MDTypography variant="body2">
                {/* {account?.annual_revenue
                  ? new Intl.NumberFormat('en-IN', {
                    style: 'currency',
                    currency: 'INR'
                  }).format(account.annual_revenue)
                  : 'N/A'} */}
                  {account?.annual_revenue ? formatCurrency(account.annual_revenue, account.currency) : ''}
              </MDTypography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            height: '100%',
            borderRadius: '16px',
            background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
            boxShadow: '10px 10px 20px #e6e6e6, -10px -10px 20px #ffffff',
            transition: 'transform 0.3s ease, box-shadow 0.3s ease',
            '&:hover': {
              transform: 'translateY(-5px)',
              boxShadow: '15px 15px 30px #e0e0e0, -15px -15px 30px #ffffff',
            }
          }}
        >
          <MDTypography variant="h6" gutterBottom fontWeight="bold" color="primary">Key Metrics</MDTypography>
          <Divider sx={{ mb: 2, borderColor: 'primary.light' }} />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <MDTypography variant="subtitle2" color="text.secondary">Total Contacts</MDTypography>
              <MDTypography variant="h4" fontWeight="bold" color="primary.main">{data.contacts.length}</MDTypography>
            </Grid>
            <Grid item xs={6}>
              <MDTypography variant="subtitle2" color="text.secondary">Active Deals</MDTypography>
              <MDTypography variant="h4" fontWeight="bold" color="primary.main">
                {data.deals.filter(deal =>
                  deal.stage !== 'Closed Won' && deal.stage !== 'Closed Lost'
                ).length}
              </MDTypography>
            </Grid>
            <Grid item xs={6}>
              <MDTypography variant="subtitle2" color="text.secondary">Open Cases</MDTypography>
              <MDTypography variant="h4" fontWeight="bold" color="primary.main">
                {data.cases.filter(caseItem =>
                  caseItem.status === 'Open' || caseItem.status === 'In Progress'
                ).length}
              </MDTypography>
            </Grid>
            {showLegacyOrders && (
            <Grid item xs={6}>
              <MDTypography variant="subtitle2" color="text.secondary">Total Orders</MDTypography>
              <MDTypography variant="h4" fontWeight="bold" color="primary.main">{data.orders.length}</MDTypography>
            </Grid>
            )}
            {showLegacyOrders && (
            <Grid item xs={6}>
              <MDTypography variant="subtitle2" color="text.secondary">Total Revenue</MDTypography>
              <MDTypography variant="h4" fontWeight="bold" color="primary.main">
                {formatCurrency(data.orders.reduce((sum, order) => sum + (parseFloat(order.total_amount) || 0), 0), account.currency)}
              </MDTypography>
            </Grid>
            )}
            <Grid item xs={6}>
              <MDTypography variant="subtitle2" color="text.secondary">Last Activity</MDTypography>
              <MDTypography variant="body2" color="text.primary">
                {data.activities.length > 0
                  ? formatDate(data.activities[0].updated_at, 'datetime')
                  : 'No recent activity'}
              </MDTypography>
            </Grid>
          </Grid>
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: '16px',
            background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
            boxShadow: '10px 10px 20px #e6e6e6, -10px -10px 20px #ffffff',
            height: '400px', // Fixed height
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <MDTypography variant="h6" gutterBottom fontWeight="bold" color="primary">
            Recent Activity Timeline
          </MDTypography>
          <Divider sx={{ mb: 2, borderColor: 'primary.light' }} />
          <Box sx={{
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              width: '8px'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '10px'
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#888',
              borderRadius: '10px',
              '&:hover': {
                background: '#555'
              }
            }
          }}>
            <TimelineList>
              {data.activities.length > 0 ? (
                data.activities
                  .slice(0, 5)
                  .map((activity) => (
                    <TimelineItem
                      key={activity.id}
                      color={
                        activity.type === 'email' ? 'primary' :
                          activity.type === 'call' ? 'success' :
                            activity.type === 'meeting' ? 'warning' :
                              activity.type === 'task' ? 'primary' :
                                'dark'
                      }
                      icon={
                        activity.type === 'email' ? <EmailIcon /> :
                          activity.type === 'call' ? <PhoneIcon /> :
                            activity.type === 'meeting' ? <EventIcon /> :
                              activity.type === 'task' ? <DescriptionIcon /> :
                                <BusinessIcon />
                      }
                      title={
                        <Typography variant="subtitle1" fontWeight="bold">
                          {activity.title}
                        </Typography>
                      }
                      dateTime={formatDate(activity.due_date, 'datetime')}
                      description={
                        <Typography variant="body2" color="text.secondary">
                          {activity.status}<br />
                          {activity.description}<br />
                        </Typography>
                      }
                      lastItem={activity.id === data.activities[data.activities.length - 1].id}
                    />
                  ))
              ) : (
                <MDTypography variant="body2" color="text">
                  No recent activities found for this account.
                </MDTypography>
              )}
            </TimelineList>
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderContacts = () => (
    <Grid container spacing={3}>
      {data.contacts.length > 0 ? (
        data.contacts.map((contact) => (
          <Grid item xs={12} sm={6} md={4} key={contact.id}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '12px',
                background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                border: '1px solid rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                  backgroundColor: 'rgba(0, 158, 219, 0.04)',
                }
              }}
              onClick={() => navigate(`/contacts/${contact.id}`)}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{
                  bgcolor: 'primary.main',
                  boxShadow: '0 4px 8px rgba(0, 158, 219, 0.2)',
                  mr: 2
                }}>
                  <PersonIcon />
                </Avatar>
                <Typography variant="subtitle1" fontWeight="bold">
                  {`${contact.first_name || ''} ${contact.middle_name || ''} ${contact.last_name || ''}`.trim()}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {contact.contact_role || 'No designation assigned'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {contact.email && (
                  <Chip
                    icon={<EmailIcon fontSize="small" />}
                    label={contact.email}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      bgcolor: 'rgba(0, 158, 219, 0.08)',
                      '&:hover': { bgcolor: 'rgba(0, 158, 219, 0.12)' }
                    }}
                  />
                )}
                {contact.mobile_number && (
                  <Chip
                    icon={<PhoneIcon fontSize="small" />}
                    label={contact.mobile_number}
                    size="small"
                    sx={{
                      borderRadius: '6px',
                      bgcolor: 'rgba(76, 175, 80, 0.08)',
                      '&:hover': { bgcolor: 'rgba(76, 175, 80, 0.12)' }
                    }}
                  />
                )}
              </Box>
            </Paper>
          </Grid>
        ))
      ) : (
        <Grid item xs={12}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            flexDirection: 'column',
            color: 'text.secondary',
            bgcolor: 'rgba(0,0,0,0.02)',
            borderRadius: '12px'
          }}>
            <PersonIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
            <MDTypography variant="body2" color="text">
              No contacts found for this account.
            </MDTypography>
          </Box>
        </Grid>
      )}
    </Grid>
  );

  const renderDeals = () => (
    <Grid container spacing={3}>
      {data.deals.length > 0 ? (
        data.deals.map((deal) => (
          <Grid item xs={12} sm={6} md={4} key={deal.id}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '12px',
                background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                border: '1px solid rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                  backgroundColor: 'rgba(0, 158, 219, 0.04)',
                }
              }}
              onClick={() => navigate(`/deals/${deal.id}`)}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{
                  bgcolor: deal.stage === 'Closed Won' ? 'success.main' : 'primary.main',
                  mr: 2
                }}>
                  <AttachMoneyIcon />
                </Avatar>
                <Typography variant="subtitle1" fontWeight="bold">
                  {deal.name}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {/* {deal.value && new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR'
                }).format(deal.value)} */}
                {deal.value ? formatCurrency(deal.value, deal.currency) : ''}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={deal.stage}
                  size="small"
                  color={deal.stage === 'Closed Won' ? 'success' : 'primary'}
                />
                {deal.expected_close_date && (
                  <Chip
                    icon={<EventIcon fontSize="small" />}
                    label={`Closing: ${formatDate(deal.expected_close_date)}`}
                    size="small"
                  />
                )}
              </Box>
            </Paper>
          </Grid>
        ))
      ) : (
        <Grid item xs={12}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            flexDirection: 'column',
            color: 'text.secondary',
            bgcolor: 'rgba(0,0,0,0.02)',
            borderRadius: '12px'
          }}>
            <AttachMoneyIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
            <MDTypography variant="body2" color="text">
              No deals found for this account.
            </MDTypography>
          </Box>
        </Grid>
      )}
    </Grid>
  );

  const renderCases = () => (
    <Grid container spacing={3}>
      {data.cases.length > 0 ? (
        data.cases.map((caseItem) => (
          <Grid item xs={12} sm={6} md={4} key={caseItem.id}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '12px',
                background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                border: '1px solid rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                  backgroundColor: 'rgba(0, 158, 219, 0.04)',
                }
              }}
              onClick={() => navigate(`/cases/${caseItem.id}`)}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{
                  bgcolor:
                    caseItem.status === 'Open' ? 'error.main' :
                      caseItem.status === 'In Progress' ? 'warning.main' :
                        'success.main',
                  mr: 2
                }}>
                  <SupportAgentIcon />
                </Avatar>
                <Typography variant="subtitle1" fontWeight="bold">
                  {caseItem.subject || `Case #${caseItem.case_number}`}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Case #{caseItem.case_number}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={caseItem.status}
                  size="small"
                  color={
                    caseItem.status === 'Open' ? 'error' :
                      caseItem.status === 'In Progress' ? 'warning' :
                        'success'
                  }
                />
                <Chip
                  label={`Priority: ${caseItem.priority}`}
                  size="small"
                />
              </Box>
            </Paper>
          </Grid>
        ))
      ) : (
        <Grid item xs={12}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            flexDirection: 'column',
            color: 'text.secondary',
            bgcolor: 'rgba(0,0,0,0.02)',
            borderRadius: '12px'
          }}>
            <SupportAgentIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
            <MDTypography variant="body2" color="text">
              No support cases found for this account.
            </MDTypography>
          </Box>
        </Grid>
      )}
    </Grid>
  );

  const renderActivities = () => (
    <Box sx={{
      height: 'calc(100vh - 300px)', // Fixed height
      overflowY: 'auto',
      '&::-webkit-scrollbar': {
        width: '8px'
      },
      '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
        borderRadius: '10px'
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#888',
        borderRadius: '10px',
        '&:hover': {
          background: '#555'
        }
      }
    }}>
      <TimelineList sx={{
        '& .MuiTimelineItem-root': {
          '&:before': {
            display: 'none'
          }
        },
        '& .MuiTimelineContent-root': {
          p: 2,
          mb: 3,
          borderRadius: '12px',
          backgroundColor: 'white',
          boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
          border: '1px solid rgba(0,0,0,0.05)',
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-3px)',
            boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
            backgroundColor: 'rgba(0, 158, 219, 0.04)'
          }
        }
      }}>
        {data.activities.length > 0 ? (
          data.activities.map((activity) => (
            <TimelineItem
              key={activity.id}
              color={
                activity.type === 'email' ? 'primary' :
                  activity.type === 'call' ? 'success' :
                    activity.type === 'meeting' ? 'warning' :
                      activity.type === 'task' ? 'primary' :
                        'dark'
              }
              icon={
                activity.type === 'email' ? <EmailIcon /> :
                  activity.type === 'call' ? <PhoneIcon /> :
                    activity.type === 'meeting' ? <EventIcon /> :
                      activity.type === 'task' ? <DescriptionIcon /> :
                        <BusinessIcon />
              }
              title={
                <Typography variant="subtitle1" fontWeight="bold">
                  {activity.title}
                </Typography>
              }
              dateTime={formatDate(activity.due_date, 'datetime')}
              description={
                <Typography variant="body2" color="text.secondary">
                  {activity.status}<br />
                  {activity.description}
                </Typography>
              }
              lastItem={activity.id === data.activities[data.activities.length - 1].id}
            />
          ))
        ) : (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            flexDirection: 'column',
            color: 'text.secondary',
            bgcolor: 'rgba(0,0,0,0.02)',
            borderRadius: '12px'
          }}>
            <TimelineIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
            <MDTypography variant="body2" color="text">
              No activities found for this account.
            </MDTypography>
          </Box>
        )}
      </TimelineList>
    </Box>
  );

  const renderDocuments = () => (
    <Grid container spacing={3}>
      {data.documents.length > 0 ? (
        data.documents.map((document) => (
          <Grid item xs={12} sm={6} md={4} key={document.id}>
            <Paper
              elevation={3}
              sx={{
                p: 2,
                borderRadius: '12px',
                background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                border: '1px solid rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                  backgroundColor: 'rgba(0, 158, 219, 0.04)',
                  cursor: 'pointer'
                },
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
              }}
              onClick={(e) => {
                e.preventDefault();
                // Check if it's an image
                const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(document.file_name);
                
                // Open in new tab with appropriate behavior
                const newWindow = window.open(document.file_url, '_blank');
                if (newWindow) {
                  newWindow.opener = null; // Security best practice
                }
              }}
            >
              <Box>
                <Box display="flex" alignItems="center" mb={2}>
                  <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                    <DescriptionIcon />
                  </Avatar>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {document.file_name}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {document.file_type} • {document.file_size ? `${Math.round(document.file_size / 1024)} KB` : 'Unknown size'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                <Chip
                  label={`Uploaded: ${formatDate(document.created_at)}`}
                  size="small"
                  sx={{ borderRadius: '6px' }}
                />
              </Box>
            </Paper>
          </Grid>
        ))
      ) : (
        <Grid item xs={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
              flexDirection: 'column',
              color: 'text.secondary',
              bgcolor: 'rgba(0,0,0,0.02)',
              borderRadius: '12px',
            }}
          >
            <DescriptionIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
            <MDTypography variant="body2" color="text">
              No documents found for this account.
            </MDTypography>
          </Box>
        </Grid>
      )}
    </Grid>
  );

  const renderOrders = () => (
    <Grid container spacing={3}>
      {data.orders.length > 0 ? (
        data.orders.map((order) => (
          <Grid item xs={12} sm={6} md={4} key={order.id}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '12px',
                background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                border: '1px solid rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                  backgroundColor: 'rgba(0, 158, 219, 0.04)',
                }
              }}
              onClick={() => navigate(`/orders/${order.id}`)}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                  <ShoppingCartIcon />
                </Avatar>
                <Typography variant="subtitle1" fontWeight="bold">
                  {order.order_number || `Order #${order.id}`}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {/* {order.total_amount && new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR'
                }).format(order.total_amount)} */}
                {order.total_amount ? formatCurrency(order.total_amount, order.currency) : ''}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={order.status}
                  size="small"
                  color={
                    order.status === 'Completed' ? 'success' :
                      order.status === 'Cancelled' ? 'error' :
                        'primary'
                  }
                />
                <Chip
                  icon={<EventIcon fontSize="small" />}
                  label={`Created: ${formatDate(order.created_at)}`}
                  size="small"
                />
              </Box>
            </Paper>
          </Grid>
        ))
      ) : (
        <Grid item xs={12}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            flexDirection: 'column',
            color: 'text.secondary',
            bgcolor: 'rgba(0,0,0,0.02)',
            borderRadius: '12px'
          }}>
            <ShoppingCartIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
            <MDTypography variant="body2" color="text">
              No orders found for this account.
            </MDTypography>
          </Box>
        </Grid>
      )}
    </Grid>
  );

  const renderInvoices = () => (
    <Grid container spacing={3}>
      {data.invoices.length > 0 ? (
        data.invoices.map((invoice) => (
          <Grid item xs={12} sm={6} md={4} key={invoice.id}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: '12px',
                background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
                border: '1px solid rgba(0,0,0,0.05)',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-3px)',
                  boxShadow: '0 5px 15px rgba(0,0,0,0.1)',
                  backgroundColor: 'rgba(0, 158, 219, 0.04)',
                }
              }}
              onClick={() => navigate(`/invoices`)}
            >
              <Box display="flex" alignItems="center" mb={2}>
                <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
                  <ReceiptIcon />
                </Avatar>
                <Typography variant="subtitle1" fontWeight="bold">
                  {invoice.invoice_number || `Invoice #${invoice.id}`}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {/* {invoice.total_amount && new Intl.NumberFormat('en-IN', {
                  style: 'currency',
                  currency: 'INR'
                }).format(invoice.total_amount)} */}
                {invoice.total_amount ? formatCurrency(invoice.total_amount, invoice.currency) : ''}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  label={`Order Number: ${invoice.order_number}`}
                  size="small"
                />
                <Chip
                  label={invoice.status}
                  size="small"
                  color={
                    invoice.status === 'Paid' ? 'success' :
                      invoice.status === 'Overdue' ? 'error' :
                        'primary'
                  }
                />
                <Chip
                  icon={<EventIcon fontSize="small" />}
                  label={`Due: ${formatDate(invoice.due_date)}`}
                  size="small"
                />
              </Box>
            </Paper>
          </Grid>
        ))
      ) : (
        <Grid item xs={12}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
            flexDirection: 'column',
            color: 'text.secondary',
            bgcolor: 'rgba(0,0,0,0.02)',
            borderRadius: '12px'
          }}>
            <ReceiptIcon sx={{ fontSize: 48, opacity: 0.3, mb: 2 }} />
            <MDTypography variant="body2" color="text">
              No invoices found for this account.
            </MDTypography>
          </Box>
        </Grid>
      )}
    </Grid>
  );


  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        {/* <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="80vh"
          sx={{
            background: 'linear-gradient(145deg, #ffffff, #f5f5f5)',
          }}
        >
          <Box
            textAlign="center"
            sx={{
              p: 4,
              borderRadius: '16px',
              background: 'white',
              boxShadow: '10px 10px 20px #e6e6e6, -10px -10px 20px #ffffff',
              maxWidth: '400px',
              width: '100%'
            }}
          >
            <CircularProgress
              size={60}
              thickness={4}
              sx={{
                color: 'primary.main',
                mb: 3
              }}
            />
            <MDTypography variant="h5" fontWeight="bold" mt={2} color="primary.main">
              Loading Customer 360° View
            </MDTypography>
            <MDTypography variant="body2" color="text.secondary" mt={1}>
              Please wait while we fetch the comprehensive customer data...
            </MDTypography>
          </Box>
        </MDBox> */}
        <DataLoadingSpinner
          entityType="customer360View"
          title="Loading Customer 360° View"
          message="Please wait while we fetch the comprehensive customer data..."
        />
        
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card sx={{
              // overflow: 'hidden',
              borderRadius: '16px',
              boxShadow: '0 8px 40px -12px rgba(0,0,0,0.2)'
            }}>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="primary"
                borderRadius="lg"
                coloredShadow="primary"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  background: 'linear-gradient(45deg, #1565C8 30%, #5B9BD5 90%)',
                  boxShadow: '0 12px 20px -10px rgba(0, 158, 219, 0.28), 0 4px 20px 0 rgba(0, 0, 0, 0.12), 0 7px 8px -5px rgba(0, 158, 219, 0.2)',
                  mt: { xs: -1, md: -3 },
                  position: 'relative',
                  zIndex: 2
                }}
              >
                <Box display="flex" alignItems="center">
                  <IconButton
                    color="primary"
                    onClick={handleBackToAccount}
                    sx={{
                      mr: 1,
                      background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
                      color: '#ffffff',
                      '&:hover': {
                        background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                        boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                        transform: 'translateY(-2px)',
                      }
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <MDTypography variant="h6" color="white" fontWeight="bold">
                    Customer 360° View: {account?.name}
                  </MDTypography>
                </Box>
                <Tooltip title="Refresh Data">
                  <IconButton
                    color="white"
                    onClick={handleRefresh}
                    sx={{
                      background: 'linear-gradient(145deg, #2c2c2c, #1a1a1a)',
                      color: '#ffffff',
                      '&:hover': {
                        background: 'linear-gradient(145deg, #1a1a1a, #2c2c2c)',
                        boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
                        transform: 'translateY(-2px)',
                      }
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                </Tooltip>
              </MDBox>

              <MDBox p={3}>
                <Box sx={{ mb: 2 }}>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    aria-label="customer 360 tabs"
                    variant="scrollable"
                    scrollButtons="auto"
                  >
                    <Tab value="summary" label="Summary" icon={<BusinessIcon />} iconPosition="start" />
                    <Tab value="contacts" label="Contacts" icon={<PersonIcon />} iconPosition="start" />
                    <Tab value="deals" label="Deals" icon={<AttachMoneyIcon />} iconPosition="start" />
                    <Tab value="cases" label="Cases" icon={<SupportAgentIcon />} iconPosition="start" />
                    <Tab value="activities" label="Activities" icon={<TimelineIcon />} iconPosition="start" />
                    <Tab value="documents" label="Documents" icon={<DescriptionIcon />} iconPosition="start" />
                    {showLegacyOrders && (
                      <Tab value="orders" label="Orders" icon={<ShoppingCartIcon />} iconPosition="start" />
                    )}
                    <Tab value="invoices" label="Invoices" icon={<ReceiptIcon />} iconPosition="start" />
                  </Tabs>
                </Box>

                <TabPanel value={tabValue} index="summary">
                  {renderAccountSummary()}
                </TabPanel>
                <TabPanel value={tabValue} index="contacts">
                  {renderContacts()}
                </TabPanel>
                <TabPanel value={tabValue} index="deals">
                  {renderDeals()}
                </TabPanel>
                <TabPanel value={tabValue} index="cases">
                  {renderCases()}
                </TabPanel>
                <TabPanel value={tabValue} index="activities">
                  {renderActivities()}
                </TabPanel>
                <TabPanel value={tabValue} index="documents">
                  {renderDocuments()}
                </TabPanel>
                {showLegacyOrders && (
                <TabPanel value={tabValue} index="orders">
                  {renderOrders()}
                </TabPanel>
                )}
                <TabPanel value={tabValue} index="invoices">
                  {renderInvoices()}
                </TabPanel>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      
    </DashboardLayout>
  );
}

export default Customer360View;